import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Fade,
} from "@mui/material";
import { motion } from "framer-motion";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import TimerIcon from "@mui/icons-material/Timer";

const EndGameModal = ({
  open,
  onClose,
  onPlayAgain,
  score,
  bestScore,
  newBestScore,
  gameType = "digitshift", // or "colorflood"
  timeMode = false, // true for time-based scores (DigitShift), false for move-based (ColorFlood)
}) => {
  const formatScore = (score) => {
    if (score === null) return "---";
    if (timeMode) {
      const mins = Math.floor(score / 60);
      const secs = score % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    }
    return `${score} moves`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#F5F0EB",
          boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          border: "2px solid #5780bd",
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 3,
          }}
        >
          {/* Goose Image */}
          <Box
            component={motion.div}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              backgroundImage: "url('/assets/images/goose.png')",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#222",
              mb: 2,
              textAlign: "center",
            }}
          >
            Congratulations!
          </Typography>

          {/* Score Display */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              maxWidth: 300,
              mb: 3,
            }}
          >
            {/* Current Score */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "white",
                borderRadius: 1,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {timeMode ? (
                  <TimerIcon sx={{ color: "#FFD700" }} />
                ) : (
                  <StarIcon sx={{ color: "#FFD700" }} />
                )}
                <Typography sx={{ fontWeight: 600, color: "#222" }}>
                  Your {timeMode ? "Time" : "Moves"}:
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, color: "#222" }}>
                {formatScore(score)}
              </Typography>
            </Box>

            {/* Best Score */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "white",
                borderRadius: 1,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                animation: newBestScore ? "pulse 1s ease-in-out" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.05)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmojiEventsIcon sx={{ color: "#FFD700" }} />
                <Typography sx={{ fontWeight: 600, color: "#222" }}>
                  Best {timeMode ? "Time" : "Moves"}:
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, color: "#222" }}>
                {formatScore(bestScore)}
              </Typography>
            </Box>
          </Box>

          {/* Message */}
          <Typography
            sx={{
              color: "#666",
              textAlign: "center",
              mb: 3,
              px: 2,
            }}
          >
            {newBestScore
              ? "New best score! Keep up the great work!"
              : "Great job! Try to beat your best score next time!"}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 0,
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            borderColor: "#5780bd",
            color: "#222",
            "&:hover": {
              borderColor: "#8a9340",
              bgcolor: "rgba(184, 194, 108, 0.1)",
            },
          }}
        >
          Close
        </Button>
        <Button
          onClick={onPlayAgain}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            bgcolor: "#5780bd",
            color: "#222",
            "&:hover": {
              bgcolor: "#8a9340",
            },
          }}
        >
          Play Again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndGameModal;
